import { useState } from 'react';
import { Show, useClerk } from '@clerk/react';
import { Redirect, useLocation } from 'wouter';
import { useGetMe, useUpdateMe, useGetMyStats, useGetMyFavorites, getGetMeQueryKey, getGetMyStatsQueryKey } from '@workspace/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { Navbar } from '@/components/Navbar';
import { GameCard } from '@/components/GameCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Trophy, Flame, Target, Calendar, Edit2, LogOut, Terminal, Zap } from 'lucide-react';
import { StreakBadges, BadgeCase } from '@/components/StreakBadges';
import { useToast } from '@/hooks/use-toast';

export default function Profile() {
  const { signOut } = useClerk();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [username, setUsername] = useState('');

  const { data: me, isLoading: meLoading } = useGetMe();
  const { data: stats, isLoading: statsLoading } = useGetMyStats();
  const { data: favorites, isLoading: favoritesLoading } = useGetMyFavorites();

  const updateMe = useUpdateMe({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetMyStatsQueryKey() });
        setEditDialogOpen(false);
        toast({ title: 'Profile updated', className: 'border-primary bg-background text-primary rounded-none font-mono uppercase tracking-widest glow-primary' });
      },
      onError: (error: any) => {
        toast({
          title: 'ERROR',
          description: error.message || 'Update failed',
          variant: 'destructive',
          className: 'border-destructive bg-background text-destructive rounded-none font-mono uppercase tracking-widest'
        });
      },
    },
  });

  const handleEditOpen = () => {
    if (me) {
      setUsername(me.username);
    }
    setEditDialogOpen(true);
  };

  const handleSave = () => {
    updateMe.mutate({ data: { username } });
  };

  const handleSignOut = () => {
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
    signOut({ redirectUrl: basePath || '/' });
  };

  const xpProgress = stats ? (stats.xp / (stats.xp + stats.xpToNextLevel)) * 100 : 0;

  return (
    <>
      <Show when="signed-out">
        <Redirect to="/sign-in" />
      </Show>
      <Show when="signed-in">
        <div className="min-h-[100dvh] bg-background">
          <Navbar />

          <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
            {meLoading || statsLoading ? (
              <div className="space-y-8 animate-pulse">
                <Skeleton className="h-48 w-full rounded-none border border-primary/20 bg-muted/50" />
                <Skeleton className="h-64 w-full rounded-none border border-primary/20 bg-muted/50" />
              </div>
            ) : (
              <div className="animate-slide-up">
                {/* Profile Header */}
                <div className="mb-10 relative overflow-hidden bg-background border-2 border-primary/50 p-6 sm:p-8 shadow-[0_0_30px_rgba(0,217,255,0.15)]">
                  <div className="absolute inset-0 bg-[linear-gradient(hsl(189_100%_50%_/_0.1)_1px,_transparent_1px),_linear-gradient(90deg,_hsl(189_100%_50%_/_0.1)_1px,_transparent_1px)] bg-[size:20px_20px] opacity-20 pointer-events-none" />
                  
                  <div className="relative z-10 flex flex-col items-start gap-8 sm:flex-row sm:items-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary/20 blur-xl rounded-none pointer-events-none" />
                      <Avatar className="h-28 w-28 border-4 border-primary rounded-none shadow-[0_0_20px_rgba(0,217,255,0.4)]">
                        <AvatarImage src={me?.avatarUrl || undefined} alt={me?.username} className="object-cover" />
                        <AvatarFallback className="bg-background text-primary font-display text-4xl rounded-none">
                          {me?.username?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </div>

                    <div className="flex-1">
                      <div className="mb-4 flex flex-wrap items-center gap-4">
                        <h1 className="text-4xl sm:text-5xl font-black font-display uppercase tracking-widest text-shadow-neon-cyan">
                          {me?.username}
                        </h1>
                        <div className="flex items-center gap-2 rounded-none bg-primary/20 border border-primary px-3 py-1 text-sm font-bold font-mono tracking-widest text-primary glow-primary shadow-[inset_0_0_10px_rgba(0,217,255,0.2)]">
                          LVL {me?.level}
                        </div>
                        <StreakBadges milestones={me?.streakBadges} size="md" />
                      </div>

                      <div className="mb-6 max-w-md">
                        <div className="mb-2 flex items-center justify-between text-xs font-mono uppercase tracking-widest">
                          <span className="text-muted-foreground">
                            {stats?.xp.toLocaleString()} / {(stats ? stats.xp + stats.xpToNextLevel : 0).toLocaleString()} XP
                          </span>
                          <span className="text-primary font-bold">NEXT LEVEL</span>
                        </div>
                        <div className="h-2 w-full bg-primary/10 overflow-hidden border border-primary/20">
                          <div 
                            className="h-full bg-primary shadow-[0_0_10px_rgba(0,217,255,0.8)] transition-all duration-500"
                            style={{ width: `${xpProgress}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-6 text-sm font-mono uppercase tracking-widest">
                        <div className="flex items-center gap-2">
                          <Flame className="h-5 w-5 text-primary" />
                          <span className="font-bold text-primary text-lg">{me?.currentStreak || 0}</span>
                          <span className="text-muted-foreground">DAY STREAK</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Trophy className="h-5 w-5 text-primary" />
                          <span className="font-bold text-lg">{stats?.gamesPlayed || 0}</span>
                          <span className="text-muted-foreground">SESSIONS</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Target className="h-5 w-5 text-primary" />
                          <span className="font-bold text-lg">{stats ? Math.round(stats.accuracy * 100) : 0}%</span>
                          <span className="text-muted-foreground">ACCURACY</span>
                        </div>
                        {stats?.memberSince && (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-primary" />
                            <span className="text-muted-foreground">MEMBER SINCE</span>
                            <span className="font-bold text-lg">{new Date(stats.memberSince).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex sm:flex-col gap-3 w-full sm:w-auto">
                      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="flex-1 sm:flex-none rounded-none font-bold uppercase tracking-widest border-primary/50 text-primary hover:bg-primary/10 hover:border-primary transition-all" onClick={handleEditOpen} data-testid="button-edit-profile">
                            <Terminal className="mr-2 h-4 w-4" />
                            CONFIG
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-none border-2 border-primary bg-background shadow-[0_0_40px_rgba(0,217,255,0.2)]">
                          <DialogHeader>
                            <DialogTitle className="font-display uppercase tracking-widest text-2xl text-primary text-shadow-neon-cyan">PLAYER SETTINGS</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-6 py-4">
                            <div>
                              <Label htmlFor="username" className="font-mono uppercase tracking-widest text-muted-foreground text-xs mb-2 block">NEW USERNAME</Label>
                              <Input
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter username"
                                className="rounded-none border-primary/50 bg-primary/5 font-mono text-lg uppercase focus-visible:ring-primary focus-visible:border-primary focus-visible:ring-offset-0 h-12"
                                data-testid="input-username"
                              />
                            </div>
                            <Button
                              onClick={handleSave}
                              disabled={updateMe.isPending || !username.trim()}
                              className="w-full rounded-none font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 glow-primary h-12"
                              data-testid="button-save-profile"
                            >
                              SAVE CHANGES
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>

                      <Button variant="outline" className="flex-1 sm:flex-none rounded-none font-bold uppercase tracking-widest border-destructive/50 text-destructive hover:bg-destructive/10 hover:border-destructive transition-all" onClick={handleSignOut} data-testid="button-sign-out">
                        <LogOut className="mr-2 h-4 w-4" />
                        LOGOUT
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="mb-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-primary group-hover:text-shadow-neon-cyan transition-all pl-4">
                      {stats?.totalScore.toLocaleString() || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">TOTAL POINTS</div>
                  </div>
                  <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4">
                      {me?.bestStreak || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">BEST STREAK</div>
                  </div>
                  <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-primary group-hover:text-shadow-neon-cyan transition-all pl-4">
                      {stats?.gamesPlayed || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">MISSIONS</div>
                  </div>
                  <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4" data-testid="stat-avg-per-game">
                      {stats && stats.gamesPlayed > 0 ? Math.round(stats.totalScore / stats.gamesPlayed).toLocaleString() : 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">AVG / GAME</div>
                  </div>
                  <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4">
                      {stats ? Math.round(stats.accuracy * 100) : 0}%
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">ACCURACY</div>
                  </div>
                  <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-primary group-hover:text-shadow-neon-cyan transition-all pl-4">
                      {stats?.dailiesPlayed || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">DAILY CONTESTS</div>
                  </div>
                  <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4">
                      {stats?.avgRoundTimeMs ? `${(stats.avgRoundTimeMs / 1000).toFixed(1)}s` : '--'}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">AVG ANSWER TIME</div>
                  </div>
                  <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-primary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-primary group-hover:text-shadow-neon-cyan transition-all pl-4">
                      {stats?.xp.toLocaleString() || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">TOTAL XP</div>
                  </div>
                  <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default" data-testid="stat-first-places">
                    <div className="absolute top-0 left-0 w-2 h-full bg-[#FFD700]" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-[#FFD700] pl-4">
                      {stats?.firstPlaces || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">1ST PLACE RECORDS</div>
                  </div>
                  <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default" data-testid="stat-second-places">
                    <div className="absolute top-0 left-0 w-2 h-full bg-gray-300" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-gray-300 pl-4">
                      {stats?.secondPlaces || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">2ND PLACE RECORDS</div>
                  </div>
                  <div className="relative group overflow-hidden border border-primary/30 bg-primary/5 p-6 hover:border-primary hover:bg-primary/10 transition-all cursor-default" data-testid="stat-third-places">
                    <div className="absolute top-0 left-0 w-2 h-full bg-[#CD7F32]" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-[#CD7F32] pl-4">
                      {stats?.thirdPlaces || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">3RD PLACE RECORDS</div>
                  </div>
                  <div className="relative group overflow-hidden border border-secondary/30 bg-secondary/5 p-6 hover:border-secondary hover:bg-secondary/10 transition-all cursor-default">
                    <div className="absolute top-0 left-0 w-2 h-full bg-secondary" />
                    <div className="mb-2 text-4xl font-black font-display tracking-tighter text-secondary group-hover:text-shadow-neon-purple transition-all pl-4">
                      {stats?.gameBreakdown.length || 0}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground pl-4">GAMES EXPLORED</div>
                  </div>
                </div>

                {/* Badge Case */}
                <div className="mb-12">
                  <h2 className="mb-6 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-border/50 pb-4 flex items-center gap-3">
                    <Flame className="h-8 w-8 text-primary" />
                    BADGE CASE
                  </h2>
                  <BadgeCase milestones={me?.streakBadges} currentStreak={me?.currentStreak} />
                </div>

                {/* Per-game breakdown */}
                {stats && stats.gameBreakdown.length > 0 && (
                  <div className="mb-12">
                    <h2 className="mb-6 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-border/50 pb-4 flex items-center gap-3">
                      <Target className="h-8 w-8 text-primary" />
                      GAME RECORD
                    </h2>
                    <div className="overflow-x-auto border border-card-border">
                      <table className="w-full text-left font-mono text-sm">
                        <thead>
                          <tr className="border-b border-card-border text-xs uppercase tracking-widest text-muted-foreground">
                            <th className="p-4">Game</th>
                            <th className="p-4">Sport</th>
                            <th className="p-4 text-right">Plays</th>
                            <th className="p-4 text-right">Best Score</th>
                            <th className="p-4 text-right">Total Points</th>
                            <th className="p-4 text-right">Accuracy</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.gameBreakdown.map((g) => (
                            <tr
                              key={g.gameSlug}
                              className="border-b border-card-border/50 hover:bg-primary/5 transition-colors cursor-pointer"
                              onClick={() => navigate(`/leaderboards?game=${encodeURIComponent(g.gameSlug)}`)}
                              title={`View ${g.gameTitle} all-time leaderboard`}
                              data-testid={`link-game-record-${g.gameSlug}`}
                            >
                              <td className="p-4 font-bold font-display uppercase tracking-wider">
                                <span className="inline-flex items-center gap-2">
                                  {g.gameTitle}
                                  {g.podiumRank != null && g.podiumRank <= 3 && (
                                    <span title={`Ranked #${g.podiumRank} all-time`} data-testid={`podium-rank-${g.gameSlug}`}>
                                      <Trophy
                                        className={`h-4 w-4 ${g.podiumRank === 1 ? 'text-[#FFD700]' : g.podiumRank === 2 ? 'text-gray-300' : 'text-[#CD7F32]'}`}
                                      />
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="p-4 uppercase text-muted-foreground">{g.sport}</td>
                              <td className="p-4 text-right">{g.plays}</td>
                              <td className="p-4 text-right text-primary font-bold">{g.bestScore.toLocaleString()}</td>
                              <td className="p-4 text-right">{g.totalScore.toLocaleString()}</td>
                              <td className="p-4 text-right">{Math.round(g.accuracy * 100)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent Plays & Favorites Grid */}
                <div className="grid lg:grid-cols-2 gap-12 mb-12">
                  {/* Recent Plays */}
                  {stats && stats.recentPlays.length > 0 && (
                    <div>
                      <h2 className="mb-6 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-border/50 pb-4 flex items-center gap-3">
                        <Zap className="h-8 w-8 text-primary" />
                        RECENT ACTIVITY
                      </h2>
                      <div className="space-y-4">
                        {stats.recentPlays.map((play, idx) => (
                          <div
                            key={idx}
                            className="group flex items-center justify-between border border-card-border bg-card p-4 hover:border-primary/50 transition-colors"
                          >
                            <div>
                              <h3 className="mb-1 font-bold font-display uppercase tracking-widest text-lg group-hover:text-primary transition-colors">{play.gameTitle}</h3>
                              <p className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                                &gt; {play.correctCount} / {play.totalRounds} HIT
                              </p>
                            </div>
                            <div className="text-right">
                              <div className="mb-1 text-2xl font-black font-display tracking-tighter text-primary">
                                {play.score}
                              </div>
                              <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                                {new Date(play.playedAt).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Favorites */}
                  <div>
                    {favoritesLoading ? (
                      <div>
                        <Skeleton className="mb-6 h-10 w-64 bg-primary/20" />
                        <div className="grid gap-6 sm:grid-cols-2">
                          {[...Array(2)].map((_, i) => (
                            <Skeleton key={i} className="h-80 rounded-none border border-primary/20 bg-muted/50" />
                          ))}
                        </div>
                      </div>
                    ) : favorites && favorites.length > 0 ? (
                      <div>
                        <h2 className="mb-6 text-3xl font-bold font-display uppercase tracking-widest border-b-2 border-border/50 pb-4 flex items-center gap-3">
                          <Flame className="h-8 w-8 text-secondary" />
                          FAVORITES
                        </h2>
                        <div className="grid gap-6 sm:grid-cols-2 stagger-children">
                          {favorites.map((game) => (
                            <GameCard key={game.slug} game={game} />
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Show>
    </>
  );
}
